import { create } from 'zustand'

import matchday from '@/content/matchday.json'
import { payout } from '@/lib/game'
import type { BetExperience, BetPick, Phase, PhoneMatch, PhoneState } from '@/types/game'

/**
 * Ein lokaler Spielstand zum Durchklicken aller Screens.
 *
 * WETTBANDIT-36 portiert die Screens, WETTBANDIT-24 bindet sie an Server und
 * Socket an. Dieser Store hat genau dieselbe Form wie die Antwort von
 * GET /api/state - der Austausch betrifft später nur die Datenquelle, nicht die
 * Screens.
 *
 * Er lebt ausschließlich im Entwicklungsmodus.
 */
interface MockGameState extends PhoneState {
    setPhase: (phase: Phase) => void
    setRevealIndex: (index: number) => void
    setPick: (position: number, pick: BetPick) => void
    setSurvey: (experience: BetExperience, knowledge: number) => void
    setAssessment: (guess: number, confidence: number) => void
    fillExampleBets: () => void
    /** Alle Spiele ausgelassen - der Fall ohne eine einzige Wette (WETTBANDIT-68). */
    skipAllBets: () => void
    reset: () => void
}

function initialMatches(): PhoneMatch[] {
    return matchday.matches.map((match, index) => ({
        position: index + 1,
        kickoff: match.kickoff_time,
        home: matchday.clubs.find((club) => club.name === match.home)!,
        away: matchday.clubs.find((club) => club.name === match.away)!,
        odds: { home: match.odds_home, draw: match.odds_draw, away: match.odds_away },
        pick: null,
        revealed: false,
    }))
}

function initialState(): PhoneState {
    return {
        session: {
            id: 1,
            joinCode: 'WB2024',
            groupName: 'Gruppe 9b',
            phase: 'lobby',
            revealIndex: 0,
            resultStage: 0,
        },
        participant: {
            nickname: 'Waschbaer99',
            betExperience: null,
            knowledge: null,
            guess: null,
            confidence: null,
            betsSubmitted: false,
        },
        matches: initialMatches(),
        balance: { totalPayout: 0, totalDelta: 0, correct: 0, placed: 0, stakes: 0, skipped: 10 },
        revealedCount: 0,
        // In der Vorschau bewusst gemischt: So sieht man, dass die Tipp-Reihenfolge
        // nicht der Spielnummer folgt (WETTBANDIT-67).
        betOrder: [4, 9, 1, 7, 2, 10, 5, 3, 8, 6],
    }
}

/** Rechnet Bilanz und Aufdeckung nach - wie es sonst der Server tut. */
function recompute(state: PhoneState): PhoneState {
    const revealedCount =
        state.session.phase === 'reveal'
            ? state.session.revealIndex + 1
            : ['result', 'finished'].includes(state.session.phase)
              ? state.matches.length
              : 0

    const matches = state.matches.map((match) => {
        const revealed = match.position <= revealedCount
        const source = matchday.matches[match.position - 1]

        return {
            ...match,
            revealed,
            result: revealed ? (source?.result as PhoneMatch['result']) : undefined,
            score: revealed
                ? { home: source?.score_home ?? 0, away: source?.score_away ?? 0 }
                : undefined,
        }
    })

    let totalPayout = 0
    let correct = 0
    let placed = 0

    for (const match of matches) {
        if (match.pick === null || match.pick === 'skip' || !match.revealed) {
            continue
        }

        placed++

        if (match.pick === match.result) {
            totalPayout += payout(match.odds[match.pick])
            correct++
        }
    }

    const stakes = placed * 10

    return {
        ...state,
        matches,
        revealedCount,
        balance: {
            totalPayout: Math.round(totalPayout * 100) / 100,
            totalDelta: Math.round((totalPayout - stakes) * 100) / 100,
            correct,
            placed,
            stakes,
            skipped: matches.length - placed,
        },
    }
}

export const useMockGame = create<MockGameState>()((set) => ({
    ...initialState(),
    setPhase: (phase) =>
        set((state) =>
            recompute({ ...state, session: { ...state.session, phase, revealIndex: 0 } }),
        ),
    setRevealIndex: (index) =>
        set((state) =>
            recompute({
                ...state,
                session: {
                    ...state.session,
                    revealIndex: Math.max(0, Math.min(state.matches.length - 1, index)),
                },
            }),
        ),
    setPick: (position, pick) =>
        set((state) =>
            recompute({
                ...state,
                matches: state.matches.map((match) =>
                    match.position === position ? { ...match, pick } : match,
                ),
            }),
        ),
    setSurvey: (betExperience, knowledge) =>
        set((state) => ({
            ...state,
            participant: { ...state.participant, betExperience, knowledge },
        })),
    setAssessment: (guess, confidence) =>
        set((state) => ({ ...state, participant: { ...state.participant, guess, confidence } })),
    /** Ein Beispiel-Tippschein: Treffer, Fehltipps und ein Auslassen. */
    skipAllBets: () =>
        set((state) =>
            recompute({
                ...state,
                participant: { ...state.participant, guess: 0, confidence: 3 },
                matches: state.matches.map((match) => ({ ...match, pick: 'skip' })),
            }),
        ),
    fillExampleBets: () =>
        set((state) => {
            const picks: BetPick[] = [
                'home',
                'home',
                'away',
                'draw',
                'home',
                'home',
                'away',
                'skip',
                'draw',
                'home',
            ]

            return recompute({
                ...state,
                participant: { ...state.participant, guess: 40, confidence: 4 },
                matches: state.matches.map((match, index) => ({
                    ...match,
                    pick: picks[index] ?? null,
                })),
            })
        }),
    reset: () => set(recompute(initialState())),
}))
